// DeviceList.js
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { TextField, Select, MenuItem, FormControl, CircularProgress } from '@mui/material';
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridOverlay,
} from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDevicesAsync,
  selectDevices,
  selectDevicesLoading,
} from '../../redux/devices/getDevicesSlice';
import { filterDevicesByMarket } from '../../services/flowApiService';
import { createDeviceAsync } from '../../redux/devices/createDevicesSlice';
import { updateDeviceAsync } from '../../redux/devices/editDevicesSlice';
import { deleteDeviceAsync } from '../../redux/devices/deleteDevicesSlice';
import { dataGridWrapTextStyles } from '../../styles/dataGridStyles';

// Custom loading overlay for DataGrid
function CustomLoadingOverlay() {
  return (
    <GridOverlay sx={{ backgroundColor: 'background.default' }}>
      <CircularProgress size="4rem" sx={{ color: 'secondary.main' }} />
    </GridOverlay>
  );
}

function EditToolbar({ setRows, setRowModesModel, isProcessing }) {
  const handleClick = () => {
    const id = `new_${Date.now()}`;
    setRows((old) => [
      {
        id,
        device_name: '',
        device_type: 'iOS',
        primary_device_id: '',
        secondary_device_id: '',
        serial_number: '',
        is_active: true,
        isNew: true,
      },
      ...old,
    ]);
    setRowModesModel((old) => ({
      ...old,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'device_name' },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={isProcessing}
        sx={{
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        Add Device
      </Button>
    </GridToolbarContainer>
  );
}

export default function DeviceList() {
  const dispatch = useDispatch();
  // Get all devices from Redux (unfiltered)
  const allDevices = useSelector(selectDevices);
  const loading = useSelector(selectDevicesLoading);

  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [editingDeviceTypes, setEditingDeviceTypes] = React.useState({});
  const [originalRows, setOriginalRows] = React.useState({});
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [shouldAnimateRows, setShouldAnimateRows] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [selectedMarket, setSelectedMarket] = React.useState(null);
  const isInitialMount = React.useRef(true);
  const spinnerWasShown = React.useRef(false);
  const isLoadingRef = React.useRef(false);

  // Normalize device type casing to match dropdown options
  const normalizeDeviceType = (type) => {
    if (!type) return 'iOS';
    const lower = type.toLowerCase();
    if (lower === 'ios') return 'iOS';
    if (lower === 'android') return 'Android';
    if (lower === 'web') return 'Web';
    return type;
  };

  // Control spinner visibility with delay to prevent flicker on fast loads
  React.useEffect(() => {
    let timer;
    if (loading) {
      if (isInitialMount.current) {
        // On initial mount, show spinner immediately
        setShowSpinner(true);
        spinnerWasShown.current = true;
        isInitialMount.current = false;
      } else {
        // On subsequent loads, delay spinner to prevent flicker
        timer = setTimeout(() => {
          setShowSpinner(true);
          spinnerWasShown.current = true;
        }, 300);
      }
    } else {
      // When loading finishes, if spinner was shown during THIS load, enable row animation
      if (spinnerWasShown.current) {
        // Force animation restart by toggling off then on
        setShouldAnimateRows(false);
        requestAnimationFrame(() => {
          setShouldAnimateRows(true);
        });
      } else {
        // No spinner shown, ensure animation is off
        setShouldAnimateRows(false);
      }
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  // Load devices from database on mount
  React.useEffect(() => {
    const loadDevices = async () => {
      // Prevent concurrent calls
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      // Clear rows before loading
      setRows([]);
      setShouldAnimateRows(false);
      spinnerWasShown.current = false;

      // Get current market from localStorage
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;
      setSelectedMarket(storedMarket);

      try {
        await dispatch(getDevicesAsync());
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadDevices();
  }, [dispatch]);

  // Listen for market changes
  React.useEffect(() => {
    const handleMarketChange = () => {
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;
      setSelectedMarket(storedMarket);

      // Cancel all edit modes properly with ignoreModifications
      setRowModesModel((prevModel) => {
        const newModel = {};
        Object.keys(prevModel).forEach((id) => {
          if (prevModel[id].mode === GridRowModes.Edit) {
            newModel[id] = {
              mode: GridRowModes.View,
              ignoreModifications: true,
            };
          }
        });
        return Object.keys(newModel).length > 0 ? newModel : {};
      });

      // Clean up after cancel completes
      setTimeout(() => {
        // Remove new unsaved rows (prevents saving to wrong market)
        setRows((prevRows) => prevRows.filter((row) => !row.isNew));

        // Clear editing state
        setOriginalRows({});
        setRowModesModel({});
        setEditingDeviceTypes({});
      }, 0);
    };

    // Listen for custom event (same window)
    window.addEventListener('marketChanged', handleMarketChange);

    // Listen for storage events (cross-tab)
    const handleStorageChange = (e) => {
      if (e.key === 'market') {
        handleMarketChange();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('marketChanged', handleMarketChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update rows when devices are loaded and filter by market
  React.useEffect(() => {
    if (!loading && allDevices) {
      // Get current market from localStorage
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;

      // Filter devices by market
      const filteredDevices = filterDevicesByMarket(allDevices, storedMarket?.id);

      if (filteredDevices && filteredDevices.length > 0) {
        const mappedDevices = filteredDevices.map((device) => ({
          id: device.id || device.device_id, // Use database ID
          device_name: device.name,
          device_type: normalizeDeviceType(device.type),
          primary_device_id: device.device_id_1,
          secondary_device_id: device.device_id_2 || '',
          serial_number: device.device_id_3 || '',
          is_active: device.is_active !== undefined ? device.is_active : true,
          database_id: device.id || device.device_id, // Store database ID
          isNew: false,
        }));

        // Sort by database ID (ascending) so newest devices appear at bottom
        const sortedDevices = mappedDevices.sort((a, b) => b.database_id - a.database_id);
        setRows(sortedDevices);
      } else {
        setRows([]);
      }
    }
  }, [allDevices, loading, selectedMarket]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    const row = rows.find((r) => r.id === id);
    // Store original row data for comparison
    setOriginalRows((prev) => ({
      ...prev,
      [id]: { ...row },
    }));
    setEditingDeviceTypes((prev) => ({
      ...prev,
      [id]: row.device_type,
    }));
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    // Clean up the editing device type state
    setEditingDeviceTypes((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => async () => {
    const row = rows.find((r) => r.id === id);
    if (!row) {
      console.error('Row not found for deletion');
      return;
    }

    if (window.confirm(`Delete device "${row.device_name || 'Unnamed Device'}"?`)) {
      setIsProcessing(true);
      try {
        // Only delete from database if it has a database_id
        if (row.database_id) {
          const result = await dispatch(deleteDeviceAsync(row.database_id));
          if (!result.success) {
            alert(`Failed to delete device: ${result.error}`);
            setIsProcessing(false);
            return;
          }
        }

        // Remove from UI
        setRows((prev) => prev.filter((r) => r.id !== id));
      } catch (error) {
        console.error('Error deleting device:', error);
        alert(error.message || 'Failed to delete device');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    // Clean up the editing device type state
    setEditingDeviceTypes((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });

    // Clean up original row tracking
    setOriginalRows((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });

    const edited = rows.find((r) => r.id === id);
    if (edited.isNew) {
      setRows((prev) => prev.filter((r) => r.id !== id));
    }
  };

  // Convert UI device type format to database format
  const convertDeviceTypeToDbFormat = (type) => {
    if (!type) return 'ios';
    return type.toLowerCase(); // "iOS" -> "ios", "Android" -> "android", "Web" -> "web"
  };

  const processRowUpdate = async (newRow) => {
    setIsProcessing(true);
    try {
      const deviceName = newRow.device_name?.trim();
      const primaryDeviceId = newRow.primary_device_id?.trim();
      const secondaryDeviceId = newRow.secondary_device_id?.trim() || null;
      const serialNumber = newRow.serial_number?.trim() || null;
      const deviceType = newRow.device_type;

      // Validate required fields
      if (newRow.isNew && (!deviceName || !primaryDeviceId)) {
        alert('Device Name and Primary Device ID are required.');
        throw new Error('Missing required fields');
      }

      // Validate secondary device ID for iOS devices
      if (deviceType === 'iOS' && (!secondaryDeviceId || secondaryDeviceId === '')) {
        alert('Secondary Device ID (IDFV) is required for iOS devices.');
        throw new Error('Missing secondary device ID for iOS');
      }

      // Enforce allowed characters for device_name
      const namePattern = /^[A-Za-z0-9_-]+$/;
      if (!namePattern.test(deviceName)) {
        alert(
          'Device Name may only contain letters, numbers, underscores, or hyphens (no spaces or special characters).',
        );
        throw new Error('Invalid device_name');
      }

      // Validate device type
      if (!['iOS', 'Android', 'Web'].includes(deviceType)) {
        alert('Please select a valid device type (iOS, Android, or Web).');
        throw new Error('Invalid device_type');
      }

      // For non-iOS devices, secondary ID should be null
      const finalSecondaryId = deviceType === 'iOS' ? secondaryDeviceId : null;
      console.log('new row', newRow);
      if (newRow.isNew) {
        // CREATE: Send all fields
        const createPayload = {
          name: deviceName,
          type: convertDeviceTypeToDbFormat(deviceType),
          device_id_1: primaryDeviceId,
          device_id_2: finalSecondaryId,
          device_id_3: serialNumber,
        };

        const result = await dispatch(createDeviceAsync(createPayload));

        if (!result.success) {
          throw new Error(result.error || 'Failed to create device');
        }

        // Refresh device list to get the new device with its database ID
        await dispatch(getDevicesAsync());

        return newRow; // Return for DataGrid to update optimistically
      } else {
        // UPDATE: Only send changed fields
        const originalRow = originalRows[newRow.id];
        console.log('originalRows', originalRows);

        if (!originalRow) {
          console.error('Original row data missing for device', newRow.id);
          throw new Error(
            'Cannot update device: original data not found. Please cancel and try again.',
          );
        }

        const updatePayload = {};

        if (deviceName !== originalRow.device_name) {
          updatePayload.name = deviceName;
        }
        if (deviceType !== originalRow.device_type) {
          updatePayload.type = convertDeviceTypeToDbFormat(deviceType);
        }
        if (primaryDeviceId !== originalRow.primary_device_id) {
          updatePayload.device_id_1 = primaryDeviceId;
        }
        if (finalSecondaryId !== originalRow.secondary_device_id) {
          updatePayload.device_id_2 = finalSecondaryId;
        }
        if (serialNumber !== (originalRow.serial_number || null)) {
          updatePayload.device_id_3 = serialNumber;
        }
        if (newRow.is_active !== originalRow.is_active) {
          updatePayload.is_active = newRow.is_active;
        }

        // Only make API call if there are changes
        if (Object.keys(updatePayload).length > 0) {
          const result = await dispatch(updateDeviceAsync(newRow.database_id, updatePayload));

          if (!result.success) {
            throw new Error(result.error || 'Failed to update device');
          }
        }

        // Clean up original row tracking
        setOriginalRows((prev) => {
          const newState = { ...prev };
          delete newState[newRow.id];
          return newState;
        });

        const updatedRow = {
          ...newRow,
          device_name: deviceName,
          device_type: deviceType,
          primary_device_id: primaryDeviceId,
          secondary_device_id: finalSecondaryId,
          serial_number: serialNumber,
          isNew: false,
        };

        setRows((prev) => prev.map((r) => (r.id === newRow.id ? updatedRow : r)));

        return updatedRow;
      }
    } catch (error) {
      console.error('Error saving device:', error);
      alert(error.message || 'Failed to save device');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRowModesModelChange = (newModel) => {
    setRowModesModel(newModel);
  };

  const columns = [
    {
      field: 'device_name',
      headerName: 'Device Name',
      flex: 1,
      editable: true,
      cellClassName: 'wrap-text',
      minWidth: 140,
      renderEditCell: (params) => {
        if (!params.row.isNew) {
          return <Box sx={{ pl: 1 }}>{params.row.device_name}</Box>;
        }
        return (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={params.value || ''}
            onChange={(e) =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              })
            }
            sx={{
              fontSize: '0.875rem',
              p: 0,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                padding: '4px 8px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        );
      },
    },
    {
      field: 'device_type',
      headerName: 'Device Type',
      flex: 0.8,
      editable: true,
      minWidth: 110,
      renderEditCell: (params) => (
        <FormControl size="small" fullWidth>
          <Select
            value={editingDeviceTypes[params.id] ?? params.value ?? 'iOS'}
            onChange={(e) => {
              const newDeviceType = e.target.value;

              // Update our tracking state
              setEditingDeviceTypes((prev) => ({
                ...prev,
                [params.id]: newDeviceType,
              }));

              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newDeviceType,
              });

              // Clear secondary device ID if switching away from iOS
              if (newDeviceType !== 'iOS') {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'secondary_device_id',
                  value: '',
                });
              }
            }}
            sx={{
              '& fieldset': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          >
            <MenuItem value="iOS">iOS</MenuItem>
            <MenuItem value="Android">Android</MenuItem>
            <MenuItem value="Web">Web</MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: 'primary_device_id',
      headerName: 'Primary ID (IDFA/GAID)',
      flex: 1.5,
      editable: true,
      cellClassName: 'wrap-text',
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{params.value}</Box>
      ),
    },
    {
      field: 'secondary_device_id',
      headerName: 'Secondary ID (IDFV for iOS)',
      flex: 1.5,
      editable: true,
      cellClassName: 'wrap-text',
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: params.row.device_type !== 'iOS' ? 'text.disabled' : 'text.primary',
          }}
        >
          {params.value || 'N/A'}
        </Box>
      ),
      renderEditCell: (params) => {
        // Use our tracking state for the current device type during editing
        const currentDeviceType = editingDeviceTypes[params.id] ?? params.row.device_type;

        if (currentDeviceType !== 'iOS') {
          return <Box sx={{ pl: 1, color: 'text.disabled' }}>N/A</Box>;
        }
        return (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={params.value || ''}
            placeholder="Required IDFV for iOS"
            required
            onChange={(e) =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              })
            }
            sx={{
              fontSize: '0.875rem',
              p: 0,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                padding: '4px 8px',
                '& fieldset': {
                  border: 'none',
                  borderLeft:
                    currentDeviceType === 'iOS'
                      ? (theme) => `3px solid ${theme.palette.primary.main}`
                      : 'none',
                },
              },
            }}
          />
        );
      },
    },
    {
      field: 'serial_number',
      headerName: 'Serial Number (used for Appium)',
      flex: 1.2,
      editable: true,
      cellClassName: 'wrap-text',
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{params.value || '—'}</Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      type: 'boolean',
      flex: 0.5,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 90,
      getActions: ({ id }) => {
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isEditing) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  // Error handler for processRowUpdate
  const handleProcessRowUpdateError = React.useCallback((error) => {
    console.error('Process row update error:', error);
    // Error is already shown via alert() in processRowUpdate
    // This prevents DataGrid's default error behavior
  }, []);

  return (
    <Box sx={{ height: '90%', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={showSpinner || isProcessing}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        slots={{
          toolbar: EditToolbar,
          loadingOverlay: CustomLoadingOverlay,
        }}
        slotProps={{ toolbar: { setRows, setRowModesModel, isProcessing } }}
        sx={{
          ...dataGridWrapTextStyles,
          '& .MuiCheckbox-root': {
            color: 'text.secondary',
            '&.Mui-checked': {
              color: 'secondary.main',
            },
          },
          '& .MuiDataGrid-row': {
            animation: shouldAnimateRows ? 'fadeInRow 0.4s ease-in-out' : 'none',
          },
          '@keyframes fadeInRow': {
            from: {
              opacity: 0,
            },
            to: {
              opacity: 1,
            },
          },
        }}
      />
    </Box>
  );
}
